import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, TrendingUp, Clock, Bookmark, BookmarkCheck } from "lucide-react";
import { useState } from "react";

interface RouteSuggestion {
  id: string;
  name: string;
  description: string | null;
  distance_km: number;
  elevation_gain_m: number | null;
  difficulty: string;
  ai_score?: number;
  ai_description?: string;
  highlights?: string[];
  estimated_time_min?: number;
}

interface RouteSuggestionsProps {
  suggestions: RouteSuggestion[];
  onSave: (routeId: string) => void;
  onUnsave: (routeId: string) => void;
}

export const RouteSuggestions = ({
  suggestions,
  onSave,
  onUnsave,
}: RouteSuggestionsProps) => {
  const [savedRoutes, setSavedRoutes] = useState<Set<string>>(new Set());

  const handleToggleSave = (routeId: string) => {
    if (savedRoutes.has(routeId)) {
      setSavedRoutes((prev) => {
        const next = new Set(prev);
        next.delete(routeId);
        return next;
      });
      onUnsave(routeId);
    } else {
      setSavedRoutes((prev) => new Set(prev).add(routeId));
      onSave(routeId);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case "easy":
        return "bg-green-500/10 text-green-600 border-green-500/20";
      case "medium":
        return "bg-primary/10 text-primary border-primary/20";
      case "hard":
        return "bg-red-500/10 text-red-600 border-red-500/20";
      default:
        return "bg-secondary";
    }
  };

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-sm flex items-center gap-2">
        <MapPin className="w-4 h-4" />
        Rotas Recomendadas
      </h3>
      {suggestions.map((route) => (
        <Card key={route.id} className="p-0 overflow-hidden cursor-pointer hover:shadow-lg transition-all hover:scale-[1.01]">
          <div className="flex">
            {/* Thumbnail image */}
            <div 
              className="w-24 h-24 flex-shrink-0 flex items-center justify-center relative"
              style={{
                background: route.difficulty.toLowerCase() === 'easy'
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : route.difficulty.toLowerCase() === 'medium'
                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                  : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              }}
            >
              <MapPin className="w-8 h-8 text-white/60" />
            </div>
            
            {/* Content */}
            <div className="flex-1 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{route.name}</h4>
                    {route.ai_score && route.ai_score > 80 && (
                      <Badge variant="secondary" className="text-xs">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        {route.ai_score}% match
                      </Badge>
                    )}
                  </div>
              
              <p className="text-sm text-muted-foreground">
                {route.ai_description || route.description}
              </p>

              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className={getDifficultyColor(route.difficulty)}>
                  {route.difficulty}
                </Badge>
                <Badge variant="outline">
                  {route.distance_km.toFixed(1)} km
                </Badge>
                {route.elevation_gain_m && (
                  <Badge variant="outline">
                    ↗ {route.elevation_gain_m}m
                  </Badge>
                )}
                {route.estimated_time_min && (
                  <Badge variant="outline">
                    <Clock className="w-3 h-3 mr-1" />
                    ~{route.estimated_time_min} min
                  </Badge>
                )}
              </div>

                  {route.highlights && route.highlights.length > 0 && (
                    <ul className="text-xs text-muted-foreground space-y-1 mt-2">
                      {route.highlights.map((highlight, idx) => (
                        <li key={idx}>• {highlight}</li>
                      ))}
                    </ul>
                  )}
                </div>

                <Button
                  size="icon"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleSave(route.id);
                  }}
                >
                  {savedRoutes.has(route.id) ? (
                    <BookmarkCheck className="w-4 h-4 text-primary" />
                  ) : (
                    <Bookmark className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};
